import React, { useEffect, useState } from 'react';
import Popup from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';
import NumberBox from 'devextreme-react/number-box';
import TextBox from 'devextreme-react/text-box';
import notify from 'devextreme/ui/notify';
import { GrantFinancialDto } from '../../../../dtos/GrantFinancialDto';
import { GrantFundingSourceDto } from '../../../../dtos/GrantFundingSourceDto';
import {
    addFundingSource,
    deleteFundingSource,
    saveProjectRequiredBudget,
    updateFundingSource
} from '../../../../api/v2/GrantFinancialApi';
import { formatCurrency, CURRENCY_FORMAT } from '../utils/currency';
import './RiskMeter.css';

interface FinancialEditDialogProps {
    grantId: string;
    financial: GrantFinancialDto;
    visible: boolean;
    onClose: () => void;
    onSaved: () => void;
}

// Values being typed into a row that is open for editing
interface RowDraft {
    amount: number;
    source: string;
}

export default function FinancialEditDialog({ grantId, financial, visible, onClose, onSaved }: FinancialEditDialogProps) {
    // Every change is written on its own, so this holds whatever the server last returned
    const [current, setCurrent] = useState<GrantFinancialDto>(financial);
    // Held apart from `current` so a save response landing mid-edit does not snap the box back
    const [budget, setBudget] = useState<number | null>(financial.projectRequiredBudget);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draft, setDraft] = useState<RowDraft>({ amount: 0, source: '' });
    const [newRow, setNewRow] = useState<RowDraft>({ amount: 0, source: '' });
    const [isBusy, setIsBusy] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setCurrent(financial);
        setBudget(financial.projectRequiredBudget);
        setEditingId(null);
        setNewRow({ amount: 0, source: '' });
    }, [visible, financial]);

    const availableFunds = (current.c06Award ?? 0) + current.fundingSources.reduce((sum, s) => sum + (s.amount || 0), 0);
    const atLimit = current.fundingSources.length >= current.maxFundingSources;

    // Applies whatever the server returned and lets the risk meter behind the dialog refresh
    const applyResult = async (call: Promise<{ data?: GrantFinancialDto | null; error?: { message: string } | null } | null>) => {
        setIsBusy(true);
        const result = await call;
        setIsBusy(false);

        if (result?.error) {
            notify(result.error.message, 'error', 3000);
            return false;
        }

        if (result?.data) {
            setCurrent(result.data);
            onSaved();
        }

        return true;
    };

    const onBudgetChanged = async (value: number | null) => {
        if (value === budget) return;
        setBudget(value);
        await applyResult(saveProjectRequiredBudget(grantId, value));
    };

    const beginEdit = (row: GrantFundingSourceDto) => {
        setEditingId(row.id);
        setDraft({ amount: row.amount, source: row.source });
    };

    const saveRow = async (row: GrantFundingSourceDto) => {
        if (!draft.source.trim()) {
            notify('Source of funding is required', 'error', 3000);
            return;
        }

        if (await applyResult(updateFundingSource(grantId, row.id!, draft.amount || 0, draft.source.trim()))) {
            setEditingId(null);
        }
    };

    const deleteRow = async (row: GrantFundingSourceDto) => {
        if (await applyResult(deleteFundingSource(grantId, row.id!))) {
            setEditingId(null);
        }
    };

    const addRow = async () => {
        if (!newRow.source.trim()) {
            notify('Source of funding is required', 'error', 3000);
            return;
        }

        if (await applyResult(addFundingSource(grantId, newRow.amount || 0, newRow.source.trim()))) {
            setNewRow({ amount: 0, source: '' });
        }
    };

    const summaryRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };

    return (
        <Popup
            visible={visible}
            onHiding={onClose}
            showCloseButton
            dragEnabled
            title="Edit Financial Data"
            width={680}
            height="auto"
        >
            <div style={{ padding: '8px' }}>
                <div style={{ ...summaryRowStyle, marginBottom: '16px' }}>
                    <label htmlFor="project-required-budget" style={{ fontWeight: 600 }}>Project Required Budget</label>
                    <NumberBox
                        className="risk-meter-amount-input"
                        value={budget ?? undefined}
                        onValueChanged={(e) => onBudgetChanged(e.value === null || e.value === undefined ? null : Number(e.value))}
                        format={CURRENCY_FORMAT}
                        min={0}
                        showClearButton
                        disabled={isBusy}
                        width={220}
                        inputAttr={{ id: 'project-required-budget', 'aria-label': 'Project Required Budget' }}
                    />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <strong>Institutional Funds / Other</strong>
                    <span style={{ fontSize: '0.8em', color: '#666' }}>
                        {current.fundingSources.length} of {current.maxFundingSources}
                    </span>
                </div>

                <div className="risk-meter-funding-row risk-meter-funding-head">
                    <div className="risk-meter-funding-source">Source of Funding</div>
                    <div className="risk-meter-funding-amount">
                        <span className="risk-meter-funding-value">Amount</span>
                        <span className="risk-meter-funding-actions-empty" />
                    </div>
                </div>

                <div className="risk-meter-funding-row">
                    <div className="risk-meter-funding-source">C06 Award</div>
                    <div className="risk-meter-funding-amount">
                        <span className="risk-meter-funding-value">{formatCurrency(current.c06Award)}</span>
                        <span className="risk-meter-funding-actions-empty" />
                    </div>
                </div>

                {current.fundingSources.map(row => editingId === row.id ? (
                    <div key={row.id} className="risk-meter-funding-row">
                        <div className="risk-meter-funding-source">
                            <TextBox
                                value={draft.source}
                                onValueChanged={(e) => setDraft(d => ({ ...d, source: e.value }))}
                                placeholder="Source of funding"
                                maxLength={255}
                                width="100%"
                                inputAttr={{ 'aria-label': 'Source of Funding' }}
                            />
                        </div>
                        <div className="risk-meter-funding-amount">
                            <span className="risk-meter-funding-value">
                                <NumberBox
                                    className="risk-meter-amount-input"
                                    value={draft.amount}
                                    onValueChanged={(e) => setDraft(d => ({ ...d, amount: Number(e.value) || 0 }))}
                                    format={CURRENCY_FORMAT}
                                    min={0}
                                    width={150}
                                    inputAttr={{ 'aria-label': 'Amount' }}
                                />
                            </span>
                            <span className="risk-meter-funding-actions">
                                <Button icon="save" hint="Save this entry" disabled={isBusy} onClick={() => saveRow(row)} />
                                <Button icon="undo" hint="Discard changes" style={{ marginLeft: '4px' }} onClick={() => setEditingId(null)} />
                                <Button icon="trash" hint="Remove this entry" style={{ marginLeft: '4px' }} disabled={isBusy} onClick={() => deleteRow(row)} />
                            </span>
                        </div>
                    </div>
                ) : (
                    <div key={row.id} className="risk-meter-funding-row">
                        <div className="risk-meter-funding-source">{row.source}</div>
                        <div className="risk-meter-funding-amount">
                            <span className="risk-meter-funding-value">{formatCurrency(row.amount)}</span>
                            <span className="risk-meter-funding-actions">
                                <Button icon="edit" hint="Edit this entry" disabled={isBusy} onClick={() => beginEdit(row)} />
                            </span>
                        </div>
                    </div>
                ))}

                {!atLimit && (
                    <div className="risk-meter-funding-row">
                        <div className="risk-meter-funding-source">
                            <TextBox
                                value={newRow.source}
                                onValueChanged={(e) => setNewRow(r => ({ ...r, source: e.value }))}
                                placeholder="Source of funding"
                                maxLength={255}
                                width="100%"
                                inputAttr={{ 'aria-label': 'New Source of Funding' }}
                            />
                        </div>
                        <div className="risk-meter-funding-amount">
                            <span className="risk-meter-funding-value">
                                <NumberBox
                                    className="risk-meter-amount-input"
                                    value={newRow.amount}
                                    onValueChanged={(e) => setNewRow(r => ({ ...r, amount: Number(e.value) || 0 }))}
                                    format={CURRENCY_FORMAT}
                                    min={0}
                                    width={150}
                                    inputAttr={{ 'aria-label': 'New Amount' }}
                                />
                            </span>
                            <span className="risk-meter-funding-actions">
                                <Button icon="add" hint="Add this entry" type="default" stylingMode="contained" disabled={isBusy} onClick={addRow} />
                            </span>
                        </div>
                    </div>
                )}

                <div className="risk-meter-funding-row risk-meter-funding-total">
                    <div className="risk-meter-funding-source">Available Funds</div>
                    <div className="risk-meter-funding-amount">
                        <span className="risk-meter-funding-value">{formatCurrency(availableFunds)}</span>
                        <span className="risk-meter-funding-actions-empty" />
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
                    <span style={{ fontSize: '0.85em', color: '#666', marginRight: 'auto' }}>
                        Changes are saved as you make them.
                    </span>
                    <Button text="Close" stylingMode="outlined" onClick={onClose} />
                </div>
            </div>
        </Popup>
    );
}
